const FileSaver = require("file-saver");
const friendlyWords = require("friendly-words");
const JSZip = require("jszip");
const log = require("../shared_utils/log.js");
const { addPartToVM } = require("./utils/add-part");
const uuid = require("./utils/uuid.js");
const {
  Thread,
  StackFrame,
  Variable,
  Target,
} = require("./utils/scratch/object-prototypes.js");
const {
  getProjectDataFromZip,
  getSceneDataFromZip,
  createSceneZip,
  deserializeSnapshot,
  loadProjectFromZip,
  loadSceneFromSb3,
} = require("./utils/import-export-helpers.js");

async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    /* fall through to the legacy copy path */
  }
  // The S3 site is served over http, where the async clipboard API is unavailable — fall back to execCommand.
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch (e) {
    return false;
  }
}

module.exports = function store(state, emitter) {
  emitter.on("load-shared-project", (filename) => {
    const urlBase = "https://joylabz-file-uploads.s3.amazonaws.com/";
    url = `${urlBase}${filename}`;
    log("load-project-from-url", url);
    fetch(url)
      .then((response) => {
        if (response.ok) {
          return response.blob(); // Convert the response to a Blob
        } else {
          throw new Error("Network response was not ok.");
        }
      })
      .then((blob) => {
        // Convert Blob to File
        const filename = url.split("/").pop(); // Extract filename from URL
        const file = new File([blob], decodeURIComponent(filename), {
          type: blob.type,
        });
        // Now you can pass this file to your function
        loadAndSetupProject(file);
      })
      .catch((error) => {
        console.error("Error fetching the file:", error);
      });
  });

  emitter.on("share-project-confirmed", () => {
    async function fetchUploadForm(slug) {
      const apiEndpoint = "https://6kmsn2hd53mzdzbdb5r4rj6e7e0swzso.lambda-url.us-east-1.on.aws/";
      const response = await fetch(`${apiEndpoint}?slug=${encodeURIComponent(slug)}`);
      if (!response.ok) {
        throw new Error(`Upload form request failed: ${response.status}`);
      }
      return response.json();
    }
    createProjectZipFile().then((fileToUpload) => {
      const projectId = String(state.projectMetadata.id || "project");
      fetchUploadForm(projectId).then(async ({ url, fields, filename }) => {
        const form = new FormData();
        Object.entries(fields).forEach(([name, value]) => form.append(name, value));
        form.append("file", fileToUpload);
        const response = await fetch(url, {
          method: "POST",
          body: form,
        });
        if (response.ok) {
          const shareBase = "https://code.fablesandfutures.com/";
          const sharedProjectUrl = `${shareBase}#bend#sharedProject=${filename}`;
          const copied = await copyToClipboard(sharedProjectUrl);
          if (copied) {
            alert(`Project shared! Link copied to clipboard:\n\n${sharedProjectUrl}`);
          } else {
            window.prompt("Project shared! Copy this link:", sharedProjectUrl);
          }
        } else {
          console.error("File upload failed", response); 
        }
      });
    });
  });

  function createProjectZipFile(project) {
    const zip = new JSZip();
    emitter.emit("pause");
    if (state.currentHistoryItem === null) {
      emitter.emit("push-history", "export-current-scene");
    }
    const exportScenePromises = [];
    state.project.scenes.forEach((scene) => {
      const history = state.histories[scene.metadata.id];
      let snapshot = scene.snapshot;
      if (history && history.length > 0) {
        snapshot = history[history.length - 1].snapshot;
      }
      exportScenePromises.push(createSceneZip(scene, snapshot, state.vm));
    });
    const { projectMetadata } = state;
    //    const newId = friendlyWords.predicates[Math.floor(Math.random() * friendlyWords.predicates.length)] + '-' + friendlyWords.objects[Math.floor(Math.random() * friendlyWords.objects.length)]
    //  state.projectMetadata.id = newId
    return Promise.all(exportScenePromises).then((results) => {
      const metadata = {
        id: state.projectMetadata.id,
        persistentVariables: projectMetadata.persistentVariables,
        thumbnail: projectMetadata.thumbnail,
        title: projectMetadata.title,
        toolbar: state.toolbar,
      };
      const metadataString = JSON.stringify(metadata);
      zip.file(`metadata.json`, metadataString);
      results.forEach((zipFile) => {
        zip.file(`${zipFile.sceneId}.gbl`, zipFile.blob);
      });
      return zip.generateAsync({ type: "blob" });
    });
  }

  emitter.on("export-project", () => {
    log("export-project");
    createProjectZipFile(state.project).then((r) => {
      FileSaver.saveAs(r, `${state.projectMetadata.id}.gbp`);
      emitter.emit("render");
    });
  });

  function loadAndSetupProject(file) {
    loadProjectFromZip(file).then((results) => {
      state.histories = {};
      state.currentHistoryItem = null;
      state.project = {};
      state.currentScene = null;
      state.project.scenes = results.scenes;
      state.projectMetadata = results.metadata;
      if (state.projectMetadata.toolbar) {
        state.toolbar = state.projectMetadata.toolbar;
      }
      state.project.scenes.forEach((scene) => {
        state.histories[scene.metadata.id] = [];
      });
      emitter.emit("render");
    });
  }

  emitter.on("open-import-project-dialog", () => {
    log("open-import-project-dialog");
    emitter.emit("pause");
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".sb3,.gbp"; // , .sbl
    input.onchange = () => {
      const [file] = input.files;
      const { name } = file;
      const nameSplit = name.split(".");
      const extension = nameSplit[nameSplit.length - 1].toLowerCase();
      if (extension === "gbp") {
        loadAndSetupProject(input.files[0]);
      } else if (extension === "sb3") {
        //TODO : new project and load scene
        const sb3 = input.files[0];
        state.histories = {};
        state.projectMetadata = {
          category: "scratch-import",
          id: sb3.name,
          project_file: null,
          thumbnail: null,
          title: sb3.name,
        };
        state.project = { scenes: [] };
        window.location = `#${sb3.name}`;
        loadSceneFromSb3(sb3, state, emitter);
      } else {
        // eslint-disable-next-line no-alert
        alert("We can only load .sb3, .gbp files");
      }
    };
    input.click();
  });
};
